<template>
  <Stack class="crm-migration">
    <!-- Show Results View -->
    <CRMMigrationResults 
      v-if="showResults && migrationResults"
      :migration-results="migrationResults"
      :original-proposal="{ proposed_crm_data: actualMigratedData, current_crm_data: proposalData.current_crm_data }"
      :entity-external-id="entityExternalId"
      :crm-type="crmType"
      @back-to-proposal="showResults = false"
    />

    <!-- Show Proposal View -->
    <template v-else>
      <div class="tw-position-relative">
        <Button
          :to="`/advisor/entity/${$route.params.entityExternalId}`"
          class="back-button"
          icon-before="fas fa-arrow-left"
          spacing="tight"
          size="3"
        >
          Back
        </Button>
      </div>

      <Title>Review CRM Migration Proposal</Title>
      <p class="tw-text-gray-600 tw-mb-4">
        Review the proposed changes to your {{ crmType.toUpperCase() }} records based on dossier data.
      </p>

      <BoxLoader v-if="isLoading" />

      <div v-else-if="objectIsEmpty(proposalData.proposed_crm_data)">
        No CRM migration data available. Make sure the entity has dossier data and a mapping exists.
      </div>

      <template v-else>
      <!-- Value Column Labels -->
      <div class="value-labels">
        <div class="current-label">Current {{ crmType.toUpperCase() }} State</div>
        <div class="proposed-label">Proposed Updates to {{ crmType.toUpperCase() }}</div>
      </div>

      <div class="rows">
        <template
          v-for="item in allItemsWithParents"
        >
          <div
            v-if="item.type === 'parent'"
            :key="`${item.id}-parent`"
            class="row is-parent"
          >
            <div class="row-steps">
              <Icon :icon="getCRMObjectIcon(item.objectType)" class="tw-mr-2" />
              <span class="row-step-label">
                {{ item.objectType }}
              </span>
              <span class="tw-text-sm tw-text-gray-500 tw-ml-2">
                ({{ item.fieldCount }} fields)
              </span>
            </div>
          </div>

          <div
            v-else
            :key="`${item.id}-element`"
            class="row is-element"
            :class="{
              [`type-${item.type}`]: item.type,
              'is-disabled': disabledRows.includes(item.id),
            }"
          >
            <div class="row-body">
              <span class="row-path">{{ item.fieldLabel }}</span>
              <div class="row-badge">
                <Badge :status="badgeStatus(item.type)">
                  {{ item.type }}
                </Badge>
              </div>
              <div class="current-value">
                <input
                  type="text"
                  class="stk-input row-input is-fake"
                  :class="{ 'is-empty': !item.currentValue }"
                  :value="item.currentValue || 'None'"
                  readonly
                >
              </div>
              <div class="row-direction">
                <Icon icon="fas fa-arrow-right" />
              </div>
              <div class="proposed-value">
                <input
                  type="text"
                  class="stk-input row-input is-editable"
                  :class="{ 'is-empty': !item.proposedValue }"
                  :value="item.proposedValue || 'None'"
                  @input="updateProposedValue(item.id, $event.target.value)"
                >
              </div>
              <div class="row-toggle">
                <Toggle
                  :modelValue="!disabledRows.includes(item.id)"
                  @update:modelValue="() => toggleRow(item.id)"
                />
              </div>
              <div v-if="item.mappingInfo" class="row-mapping">
                <div class="mapping-button-wrapper">
                  <button
                    class="mapping-button"
                    :class="`confidence-${item.mappingInfo.confidence}`"
                  >
                    <div class="mapping-content">
                      <div class="mapping-main">
                        <strong>{{ item.mappingInfo.dossier_field_path }}</strong>
                        <div class="mapping-subtitle">
                          {{ item.mappingInfo.transformation_function || 'Direct mapping' }}
                        </div>
                      </div>
                      <div class="confidence-indicator">
                        <div class="indicator-wrapper">
                          <div class="bar-chart-icon" :class="`level-${item.mappingInfo.confidence}`">
                            <div class="bar bar-1"></div>
                            <div class="bar bar-2"></div>
                            <div class="bar bar-3"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                  <div class="mapping-tooltip">
                    {{ item.mappingInfo.reasoning || 'AI-generated field mapping' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Execute Button Container -->
      <div
        v-if="itemsThatHaveChanged.length > 0"
        class="execute-button-container"
      >
        <div class="execute-button-content">
          <i class="fas fa-sync-alt banner-icon"></i>
          <span>Ready to migrate {{ itemsThatHaveChanged.length - disabledRows.length }} fields to {{ crmType.toUpperCase() }}</span>
        </div>
        <div class="execute-button-wrapper">
          <Button
            variant="primary"
            size="5"
            @click="executeMigration"
            :disabled="isExecuting"
            :loader="isExecuting"
            class="banner-button"
          >
            {{ isExecuting ? 'Migrating...' : 'Execute Migration' }}
          </Button>
          <Button
            variant="secondary"
            size="5"
            :disabled="isExecuting"
            @click="cancelMigration"
          >
            Cancel
          </Button>
        </div>
      </div>
    </template>
    </template>
  </Stack>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { objectIsEmpty, prettifyMixedCase } from '@frontend/shared/Util.js';
import { apiFetch } from '@frontend/api/api.js';

import { Badge, BoxLoader, Icon, Title, Toggle, Stack, Button } from '@stakeui';
import CRMMigrationResults from './CRMMigrationResults.vue';

// Component setup
const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
  crmType: {
    type: String,
    default: 'salesforce',
  },
});

// Emits for handling migration operations
const emit = defineEmits(['migration-completed', 'migration-failed', 'cancel']);

// Reactive data
const proposalData = ref({});
const currentCrmData = ref({});
const isLoading = ref(false);
const isExecuting = ref(false);

// Add refs to track state
const isEnabled = ref(new Map());
const manualUpdates = ref(new Map());
const disabledRows = ref([]);

// Results view state
const showResults = ref(false);
const migrationResults = ref(null);
const actualMigratedData = ref({});

// Initialize enabled state for all changes
const initializeEnabledState = () => {
  Object.entries(proposalData.value.proposed_crm_data || {}).forEach(([objectType, data]) => {
    // Handle arrays of records (like Asset records from arrays)
    if (Array.isArray(data)) {
      data.forEach((record, recordIndex) => {
        Object.entries(record).forEach(([fieldName, proposedValue]) => {
          const recordKey = `${objectType}_${recordIndex}`;
          const key = `${recordKey}.${fieldName}`;
          // Enable all changes by default
          isEnabled.value.set(key, true);
        });
      });
    } 
    // Handle single record objects (like Contact, Account)
    else if (data && typeof data === 'object') {
      Object.entries(data).forEach(([fieldName, proposedValue]) => {
      const key = `${objectType}.${fieldName}`;
      // Enable all changes by default
      isEnabled.value.set(key, true);
    });
    }
  });
};

// Fetch current CRM data and then generate proposal
const fetchCurrentCrmData = async () => {
  try {
    const url = `/api/v1/crm/current-data/${props.entityExternalId}/?crm_type=${props.crmType}`;
    const response = await apiFetch(url);
    const data = await response.json();
    currentCrmData.value = data.current_crm_data;
    console.log('Current CRM data:', currentCrmData.value);
  } catch (error) {
    console.error('Error fetching current CRM data:', error);
    // If we can't fetch current data, proceed with empty object
    currentCrmData.value = {};
  }
};

const generateProposal = async () => {
  try {
    const payload = {
      crm_type: props.crmType,
      current_crm_data: currentCrmData.value,
    };

    const response = await apiFetch(`/api/v1/crm/migration-proposal/${props.entityExternalId}/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    
    const data = await response.json();
    proposalData.value = data;

    // Initialize enabled state for all changes
    initializeEnabledState();
  } catch (error) {
    console.error('Error generating migration proposal:', error);
    // Handle error appropriately
  }
};

// Load data when component mounts
onMounted(async () => {
  isLoading.value = true;
  try {
    await fetchCurrentCrmData();
    await generateProposal();
  } finally {
    isLoading.value = false;
  }
});

// Utility functions
const badgeStatus = type => {
  const TYPES = {
    'new field': 'success',
    'change': 'warning',
    'removal': 'danger',
    'addition': 'success',
  };

  return TYPES[type] || 'info';
};

const getCRMObjectIcon = objectType => {
  const icons = {
    'Contact': 'fas fa-user',
    'Account': 'fas fa-building',
    'Lead': 'fas fa-user-plus',
    'Opportunity': 'fas fa-handshake',
    'Case': 'fas fa-ticket-alt',
    'Task': 'fas fa-tasks',
    'Event': 'fas fa-calendar-alt',
  };
  return icons[objectType] || 'fas fa-database';
};

// Main computed property for items that have changed
const itemsThatHaveChanged = computed(() => {
  const items = [];

  Object.entries(proposalData.value.proposed_crm_data || {}).forEach(([objectType, data]) => {
    // Handle arrays of records (like Asset records from arrays)
    if (Array.isArray(data)) {
      data.forEach((record, recordIndex) => {
        Object.entries(record).forEach(([fieldName, proposedValue]) => {
          const recordKey = `${objectType}_${recordIndex}`;
          const currentValue = proposalData.value.current_crm_data?.[objectType]?.[recordIndex]?.[fieldName];

          // Only include items that have actually changed
          if (currentValue !== proposedValue && (currentValue || proposedValue)) {
            const key = `${recordKey}.${fieldName}`;
            items.push({
              id: key,
              objectType,
              recordIndex,
              fieldName,
              fieldLabel: `${prettifyMixedCase(fieldName)} (Record ${recordIndex + 1})`,
              currentValue: currentValue || 'None',
              proposedValue,
              changeType: currentValue ? 'change' : 'new',
              type: currentValue ? 'change' : 'new field',
              isEnabled: isEnabled.value.get(key),
              manualValue: manualUpdates.value.get(key),
            });
          }
        });
      });
    } 
    // Handle single record objects (like Contact, Account)
    else if (data && typeof data === 'object') {
      Object.entries(data).forEach(([fieldName, proposedValue]) => {
      const currentValue = proposalData.value.current_crm_data?.[objectType]?.[fieldName];

      // Only include items that have actually changed
      if (currentValue !== proposedValue && (currentValue || proposedValue)) {
        const key = `${objectType}.${fieldName}`;
        items.push({
          id: key,
          objectType,
          fieldName,
          fieldLabel: prettifyMixedCase(fieldName),
          currentValue: currentValue || 'None',
          proposedValue,
          changeType: currentValue ? 'change' : 'new',
            type: currentValue ? 'change' : 'new field',
          isEnabled: isEnabled.value.get(key),
          manualValue: manualUpdates.value.get(key),
        });
      }
    });
    }
  });

  return items;
});

// Group items with parent objects
const allItemsWithParents = computed(() => {
  const result = [];
  const processedParents = new Set();

  // Group items by their parent identifier (similar to DossierDiff.vue)
  const itemsByParentPath = {};
  
  itemsThatHaveChanged.value.forEach(item => {
    // Create parent identifier: for arrays include recordIndex, for single records just objectType
    let parentPath;
    if (item.recordIndex !== undefined) {
      // Array item - group by objectType + recordIndex (like "Asset_0", "Asset_1")
      parentPath = `${item.objectType}_${item.recordIndex}`;
    } else {
      // Single record - group by objectType (like "Contact", "Account")
      parentPath = item.objectType;
    }
    
    if (!itemsByParentPath[parentPath]) {
      itemsByParentPath[parentPath] = [];
    }
    itemsByParentPath[parentPath].push(item);
  });

  // Sort parent paths to ensure consistent ordering
  const sortedParentPaths = Object.keys(itemsByParentPath).sort((a, b) => {
    // Extract object type and record index for comparison
    const parseParent = (parentPath) => {
      const parts = parentPath.split('_');
      const objectType = parts[0];
      const recordIndex = parts.length > 1 ? parseInt(parts[1]) : -1;
      return { objectType, recordIndex };
    };
    
    const aInfo = parseParent(a);
    const bInfo = parseParent(b);
    
    // First sort by object type
    if (aInfo.objectType !== bInfo.objectType) {
      return aInfo.objectType.localeCompare(bInfo.objectType);
    }
    
    // Then sort by record index (single records come first with -1)
    return aInfo.recordIndex - bInfo.recordIndex;
  });

  // Process parent paths in order
  sortedParentPaths.forEach(parentPath => {
    if (!processedParents.has(parentPath)) {
      processedParents.add(parentPath);

      // Parse parent path to get display info
      const parts = parentPath.split('_');
      const objectType = parts[0];
      const recordIndex = parts.length > 1 ? parseInt(parts[1]) : undefined;

      // Create parent row
      const parentDisplayName = recordIndex !== undefined 
        ? `${objectType} (Record ${recordIndex + 1})`
        : objectType;

      result.push({
        id: `${parentPath}_parent`,
        type: 'parent',
        objectType: parentDisplayName,
        fieldCount: itemsByParentPath[parentPath].length,
      });

      // Add all fields for this parent, sorted by field name
      const sortedFields = itemsByParentPath[parentPath].sort((a, b) =>
        a.fieldName.localeCompare(b.fieldName)
      );

      sortedFields.forEach(item => {
        result.push(item);
      });
    }
  });

  return result;
});

const toggleRow = itemId => {
  console.log('toggleRow', itemId);
  const index = disabledRows.value.indexOf(itemId);

  if (index > -1) {
    // Remove value if it exists (enable the row)
    disabledRows.value.splice(index, 1);
    isEnabled.value.set(itemId, true);
  } else {
    // Add value if it doesn't exist (disable the row)
    disabledRows.value.push(itemId);
    isEnabled.value.set(itemId, false);
  }
};

// Function to update proposed values from user input
const updateProposedValue = (itemId, newValue) => {
  manualUpdates.value.set(itemId, newValue === 'None' ? null : newValue);
};

// Execute the migration with enabled changes
const executeMigration = async () => {
  try {
    isExecuting.value = true;

    // Filter to only include enabled changes
    const enabledChanges = {};
    Object.entries(proposalData.value.proposed_crm_data || {}).forEach(([objectType, data]) => {
      // Handle arrays of records (like Asset records from arrays)
      if (Array.isArray(data)) {
        const enabledRecords = [];
        data.forEach((record, recordIndex) => {
          const enabledRecord = {};
          Object.entries(record).forEach(([fieldName, proposedValue]) => {
            const recordKey = `${objectType}_${recordIndex}`;
            const key = `${recordKey}.${fieldName}`;
            if (isEnabled.value.get(key)) {
              // Use manual update if available, otherwise use proposed value
              enabledRecord[fieldName] = manualUpdates.value.get(key) || proposedValue;
            }
          });
          // Only include records that have enabled fields
          if (Object.keys(enabledRecord).length > 0) {
            enabledRecords.push(enabledRecord);
          }
        });
        // Only include object types that have enabled records
        if (enabledRecords.length > 0) {
          enabledChanges[objectType] = enabledRecords;
        }
      } 
      // Handle single record objects (like Contact, Account)
      else if (data && typeof data === 'object') {
      enabledChanges[objectType] = {};
        Object.entries(data).forEach(([fieldName, proposedValue]) => {
        const key = `${objectType}.${fieldName}`;
        if (isEnabled.value.get(key)) {
          // Use manual update if available, otherwise use proposed value
          enabledChanges[objectType][fieldName] = manualUpdates.value.get(key) || proposedValue;
        }
      });
      // Remove empty objects
      if (Object.keys(enabledChanges[objectType]).length === 0) {
        delete enabledChanges[objectType];
        }
      }
    });

    const payload = {
      crm_type: props.crmType,
      proposal_data: enabledChanges,
    };

    const response = await apiFetch(`/api/v1/crm/execute-migration/${props.entityExternalId}/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    
    const data = await response.json();
    console.log('Migration executed:', data);

    // Store the migration results and actual migrated data, then show the results view
    migrationResults.value = data;
    actualMigratedData.value = enabledChanges;
    showResults.value = true;

  } catch (error) {
    console.error('Error executing migration:', error);
    if (window.$buefy) {
      window.$buefy.toast.open({
        message: `Failed to execute migration: ${error.message}`,
        type: 'is-danger',
        duration: 5000,
      });
    }
  } finally {
    isExecuting.value = false;
  }
};

// Function to cancel migration
const cancelMigration = () => {
  if (window.confirm('Are you sure you want to cancel this migration?')) {
    emit('cancel');
    // Redirect back or close the migration view
    window.history.back();
  }
};
</script>

<style scoped>
.back-button {
  left: 0.75rem;
  top: 0.75rem;
  position: absolute;
}

.crm-migration {
  font-family: "Inter", "Inter Variable", sans-serif;
  padding: 2em;
  position: relative;
}

.value-labels {
  display: grid;
  grid-template-columns: 1fr 10em 20em 1.25em 20em 2.5em 15em;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  padding: 0 0.5em;
}

.current-label,
.proposed-label {
  font-weight: 600;
  color: var(--stk-text-strong);
  font-size: 0.85rem;
  background-color: var(--stk-background-alt);
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  text-align: center;
}

.current-label {
  grid-column: 3; /* Aligns with current value column */
}

.proposed-label {
  grid-column: 5; /* Aligns with proposed value column */
}

.rows {
  --gap: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: var(--gap);
}

.row {
  --size: 2rem;
  --spacing: 0.25em;
  align-items: center;
  display: grid;
  gap: var(--gap);
  line-height: 1;
  grid-template-columns: 1fr;
}

.row.is-parent {
  grid-template-columns: 1fr 10em;
  padding-top: 1em;
}

.row.is-parent:first-of-type {
  padding-top: 0;
}

.row.is-disabled {
  opacity: 0.3;
}

.row.is-disabled .current-value,
.row.is-disabled .proposed-value {
  pointer-events: none;
}

.row-steps {
  border-radius: 0.5em;
  display: flex;
  align-items: center;
  gap: var(--gap);
  color: var(--stk-text-strong);
  font-weight: 600;
}

.row-body {
  background-color: var(--stk-background-alt);
  border-radius: 0.5em;
  align-items: center;
  display: grid;
  gap: var(--gap);
  grid-template-columns: 1fr 10em 20em 1.25em 20em 2.5em 15em;
  padding: 0.5em;
}

.row-path {
  padding-left: 0.5em;
  font-weight: 500;
}

.row-badge {
  text-transform: capitalize;
}

.row-direction {
  color: var(--stk-primary);
}

.current-value,
.proposed-value {
  display: flex;
}

.current-value {
  justify-content: end;
}

.row-input {
  width: 100%;
}

.row-input.is-fake,
.row-input.is-empty {
  background: none;
  border-color: transparent;
  box-shadow: none;
  pointer-events: none;
}

.row-input.is-fake {
  text-align: right;
  padding-left: 0;
  padding-right: 0;
}

.row-input.is-empty {
  font-style: italic;
  opacity: 0.3;
}

.row-mapping {
  margin-left: 0.5em;
  position: relative;
  max-width: 14em;
}

.mapping-button-wrapper {
  position: relative;
  width: 100%;
}

.mapping-button {
  cursor: pointer;
  font-size: 0.8rem;
  border-radius: 0.25rem;
  padding: 0.5rem 0.75rem;
  background-color: var(--stk-background);
  border: 1px solid #e2e2e2;
  color: var(--stk-text);
  width: 100%;
  text-align: left;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.mapping-button:hover {
  border-color: #c0c0c0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.mapping-content {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.mapping-main {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  font-weight: 500;
}

.mapping-subtitle {
  font-size: 0.7rem;
  color: var(--stk-text-weak);
  font-weight: normal;
}

.confidence-indicator {
  display: flex;
  align-items: center;
  margin-left: 0.5rem;
  color: #aaa;
}

.indicator-wrapper {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.bar-chart-icon {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 0.75rem;
  margin-top: 1px;
}

.bar {
  width: 2px;
  border-radius: 1px;
}

.bar-1 {
  height: 5px;
  background-color: #ddd;
}

.bar-2 {
  height: 9px;
  background-color: #ddd;
}

.bar-3 {
  height: 13px;
  background-color: #ddd;
}

.level-high .bar-1,
.level-high .bar-2,
.level-high .bar-3 {
  background-color: #4caf50;
}

.level-medium .bar-1,
.level-medium .bar-2 {
  background-color: #4caf50;
}

.level-medium .bar-3 {
  background-color: #ddd;
}

.level-low .bar-1 {
  background-color: #4caf50;
}

.level-low .bar-2,
.level-low .bar-3 {
  background-color: #ddd;
}

.mapping-tooltip {
  position: absolute;
  top: calc(100% + 5px);
  right: 0;
  background-color: var(--stk-background-alt);
  border: 1px solid var(--stk-border);
  border-radius: 0.25rem;
  padding: 0.5rem;
  max-width: 250px;
  z-index: 10;
  font-size: 0.8rem;
  color: var(--stk-text);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s, visibility 0.2s;
}

.mapping-button-wrapper:hover .mapping-tooltip {
  opacity: 1;
  visibility: visible;
}

/* Execute button styles */
.execute-button-container {
  background-color: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 6px;
  position: sticky;
  bottom: 1rem;
  margin: 2rem 1rem 1rem 1rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.execute-button-container:hover {
  background-color: #bbdefb;
}

.execute-button-content {
  display: flex;
  align-items: center;
}

.banner-icon {
  margin-right: 10px;
  color: #2196f3;
  font-size: 1.1em;
}

.banner-button {
  background-color: #2196f3 !important;
  border-color: #1976d2 !important;
  color: white !important;
}

.banner-button:hover {
  background-color: #1976d2 !important;
  border-color: #1565c0 !important;
}

.execute-button-wrapper {
  display: flex;
  align-items: center;
  gap: 1em;
  flex-shrink: 0;
}
</style>
